import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getProductsByIds,
  updateProductsBulk,
  BulkProductUpdates,
  ProductItem,
} from '@/services/productsService';
import { productCategories } from '@/lib/productCategories';
import { FIND_SUBCATEGORIES } from '@/lib/findSubcategories';
import {
  DEFAULT_DELIMITER,
  clearFindIds,
  formatFindIds,
  parseFindIds,
  removeFindIds,
  useFindIds,
} from '@/lib/findsList';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardPaste,
  Copy,
  Eraser,
  ImageOff,
  Loader2,
  Wand2,
  X,
} from 'lucide-react';

const NO_CHANGE = '__no_change__';

type TopMode = 'none' | 'visible' | 'hidden';

const DELIMITER_OPTIONS = [
  { value: 'comma', delimiter: ', ', label: 'Comma (123, 32, 42)' },
  { value: 'space', delimiter: ' ', label: 'Space (123 32 42)' },
  { value: 'hyphen', delimiter: ' - ', label: 'Hyphen (123 - 32 - 42)' },
];

const BulkEdit = () => {
  const listIds = useFindIds();

  const [rawInput, setRawInput] = useState('');
  const [delimiterKey, setDelimiterKey] = useState<string>(DELIMITER_OPTIONS[0].value);
  const [category, setCategory] = useState<string>(NO_CHANGE);
  const [findSubcategory, setFindSubcategory] = useState<string>(NO_CHANGE);
  const [topMode, setTopMode] = useState<TopMode>('none');

  const [matches, setMatches] = useState<ProductItem[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeFromList, setRemoveFromList] = useState(true);
  const [result, setResult] = useState<{
    updated: number[];
    category?: string;
    findSubcategory?: string;
    top?: boolean;
  } | null>(null);

  // Smart parsing — commas, spaces, hyphens, newlines, pipes: all are accepted.
  const parsedIds = useMemo(() => parseFindIds(rawInput), [rawInput]);
  const rawTokenCount = useMemo(() => (rawInput.match(/\d+/g) ?? []).length, [rawInput]);
  const duplicateCount = Math.max(0, rawTokenCount - parsedIds.length);
  const parsedKey = parsedIds.join(',');

  const foundIds = useMemo(() => new Set(matches.map((item) => Number(item._id))), [matches]);
  const missingIds = useMemo(
    () => parsedIds.filter((id) => !foundIds.has(id)),
    [parsedIds, foundIds],
  );
  const orderedMatches = useMemo(() => {
    const byId = new Map(matches.map((item) => [Number(item._id), item]));
    return parsedIds.map((id) => byId.get(id)).filter(Boolean) as ProductItem[];
  }, [matches, parsedIds]);

  // Preview lookup (debounced) so the admin sees what will be changed.
  useEffect(() => {
    const ids = parsedKey ? parsedKey.split(',').map((value) => Number(value)) : [];

    if (ids.length === 0) {
      setMatches([]);
      setLoadingPreview(false);
      return;
    }

    let cancelled = false;
    setLoadingPreview(true);
    const timer = setTimeout(async () => {
      try {
        const data = await getProductsByIds(ids);
        if (!cancelled) setMatches(data);
      } catch (error) {
        console.error('Error loading bulk edit preview:', error);
        if (!cancelled) {
          setMatches([]);
          toast.error('Failed to load the selected items.');
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [parsedKey]);

  const handleLoadList = () => {
    if (listIds.length === 0) {
      toast.info('Your list is empty — click "Add to List" on the Products page first.');
      return;
    }
    const merged = parseFindIds(`${rawInput} ${formatFindIds(listIds)}`);
    setRawInput(formatFindIds(merged));
    toast.success(`Loaded ${listIds.length} ID(s) from your list`);
  };

  const handleCopy = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch (error) {
      console.error('Error copying IDs:', error);
      toast.error('Could not copy automatically — select the text and copy it manually.');
    }
  };

  const canApply =
    parsedIds.length > 0 &&
    (category !== NO_CHANGE || findSubcategory !== NO_CHANGE || topMode !== 'none');

  const handleApply = async () => {
    const updates: BulkProductUpdates = {};
    if (category !== NO_CHANGE) updates.category = category;
    if (findSubcategory !== NO_CHANGE) updates.findSubcategory = findSubcategory;
    if (topMode !== 'none') updates.top = topMode === 'visible';

    try {
      setApplying(true);
      const updatedIds = await updateProductsBulk(parsedIds, updates);

      if (removeFromList) removeFindIds(parsedIds);

      setResult({
        updated: updatedIds,
        category: updates.category,
        findSubcategory: updates.findSubcategory,
        top: updates.top,
      });
      setConfirmOpen(false);

      if (updatedIds.length === 0) {
        toast.error('No items were updated. Check that the IDs exist and try again.');
      } else if (updatedIds.length < parsedIds.length) {
        toast.success(
          `Updated ${updatedIds.length} of ${parsedIds.length} item(s) — some IDs were not found.`,
        );
      } else {
        toast.success(`Updated ${updatedIds.length} item(s)`);
      }

      const refreshed = await getProductsByIds(parsedIds);
      setMatches(refreshed);
    } catch (error) {
      console.error('Error applying bulk update:', error);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to apply the bulk update.',
      );
    } finally {
      setApplying(false);
    }
  };

  const delimiter =
    DELIMITER_OPTIONS.find((option) => option.value === delimiterKey)?.delimiter ??
    DEFAULT_DELIMITER;
  const previewText = formatFindIds(parsedIds, delimiter);
  const listText = formatFindIds(listIds, delimiter);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bulk Edit</h1>
          <p className="text-muted-foreground">
            Paste a list of product IDs, then apply the same category and visibility to all of them
            at once.
          </p>
        </div>
        <Link to="/admin/clothinglist">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Products
          </Button>
        </Link>
      </div>

      {/* Step 1 — target IDs */}
      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">1. Target IDs</CardTitle>
          <CardDescription>
            Separated by commas, spaces, hyphens or new lines — all are accepted
            (<span className="font-mono">123, 32, 42</span>,{' '}
            <span className="font-mono">123 32 42</span>,{' '}
            <span className="font-mono">123 - 32 - 42</span>).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
            placeholder="Paste IDs here… e.g. 123, 32, 42"
            className="min-h-[120px] font-mono text-sm bg-background"
          />

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={parsedIds.length > 0 ? 'secondary' : 'outline'}>
              {parsedIds.length} ID(s) parsed
            </Badge>
            {duplicateCount > 0 && (
              <Badge variant="outline">{duplicateCount} duplicate(s) removed</Badge>
            )}
            {parsedIds.length > 0 && !loadingPreview && (
              <>
                <Badge variant="outline">{foundIds.size} found</Badge>
                {missingIds.length > 0 && (
                  <Badge variant="destructive">{missingIds.length} not found</Badge>
                )}
              </>
            )}
            {loadingPreview && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking IDs…
              </span>
            )}
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 pt-2 border-t border-border/50">
            <div className="flex flex-wrap items-center gap-2 pt-3 lg:pt-0">
              <Button variant="outline" size="sm" onClick={handleLoadList}>
                <ClipboardPaste className="mr-2 h-4 w-4" />
                Load my list ({listIds.length})
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={listIds.length === 0}
                onClick={() => handleCopy(listText, `Copied ${listIds.length} ID(s) to clipboard`)}
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy list
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={listIds.length === 0}
                onClick={() => {
                  clearFindIds();
                  toast.success('Saved list cleared');
                }}
              >
                <X className="mr-2 h-4 w-4" />
                Clear list
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="delimiter" className="text-sm text-muted-foreground">
                Delimiter
              </Label>
              <Select value={delimiterKey} onValueChange={setDelimiterKey}>
                <SelectTrigger id="delimiter" className="h-8 w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DELIMITER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={parsedIds.length === 0}
                onClick={() => handleCopy(previewText, `Copied ${parsedIds.length} ID(s)`)}
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy parsed
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={rawInput.length === 0}
                onClick={() => setRawInput('')}
              >
                <Eraser className="mr-2 h-4 w-4" />
                Clear input
              </Button>
            </div>
          </div>

          {parsedIds.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                Compiled string ({parsedIds.length} IDs)
              </Label>
              <Input readOnly value={previewText} className="font-mono text-xs bg-background" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — changes */}
      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">2. New values</CardTitle>
          <CardDescription>
            Only the fields you change here are written — everything else stays untouched.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="bulk-category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="bulk-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CHANGE}>— Keep current category —</SelectItem>
                  {productCategories.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk-subcategory">Find Subcategory</Label>
              <Select value={findSubcategory} onValueChange={setFindSubcategory}>
                <SelectTrigger id="bulk-subcategory">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CHANGE}>— Keep current subcategory —</SelectItem>
                  {FIND_SUBCATEGORIES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label>Visibility</Label>
              <div className="flex flex-wrap items-center gap-5">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="bulk-top"
                    checked={topMode === 'visible'}
                    onCheckedChange={() =>
                      setTopMode((prev) => (prev === 'visible' ? 'none' : 'visible'))
                    }
                  />
                  <Label htmlFor="bulk-top" className="text-sm font-medium cursor-pointer">
                    Top Visible
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="bulk-top-off"
                    checked={topMode === 'hidden'}
                    onCheckedChange={() =>
                      setTopMode((prev) => (prev === 'hidden' ? 'none' : 'hidden'))
                    }
                  />
                  <Label htmlFor="bulk-top-off" className="text-sm font-medium cursor-pointer">
                    Hide from Top
                  </Label>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {topMode === 'none'
                  ? 'Visibility will not change.'
                  : topMode === 'visible'
                    ? 'All selected items will be marked Top Visible.'
                    : 'All selected items will be removed from Top.'}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-border/50">
            <p className="text-sm text-muted-foreground">
              {parsedIds.length === 0
                ? 'Paste at least one ID to continue.'
                : `Ready to update ${parsedIds.length} item(s).`}
            </p>
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <Button
                disabled={!canApply || applying}
                onClick={() => setConfirmOpen(true)}
                className="sm:w-auto w-full"
              >
                {applying ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Wand2 className="mr-2 h-4 w-4" />
                )}
                Apply to All ({parsedIds.length})
              </Button>
              <AlertDialogContent className="bg-background border border-border/50">
                <AlertDialogHeader>
                  <AlertDialogTitle>Apply to {parsedIds.length} item(s)?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-2 text-sm">
                      <p>
                        Category:{' '}
                        <span className="font-medium text-foreground">
                          {category === NO_CHANGE ? 'keep current' : category}
                        </span>
                      </p>
                      <p>
                        Find Subcategory:{' '}
                        <span className="font-medium text-foreground">
                          {findSubcategory === NO_CHANGE ? 'keep current' : findSubcategory}
                        </span>
                      </p>
                      <p>
                        Top Visible:{' '}
                        <span className="font-medium text-foreground">
                          {topMode === 'none'
                            ? 'keep current'
                            : topMode === 'visible'
                              ? 'yes'
                              : 'no (removed from top)'}
                        </span>
                      </p>
                      <p className="text-muted-foreground">
                        IDs: <span className="font-mono text-xs">{previewText}</span>
                      </p>
                      <div className="flex items-center space-x-2 pt-2">
                        <Checkbox
                          id="remove-from-list"
                          checked={removeFromList}
                          onCheckedChange={(checked) => setRemoveFromList(checked === true)}
                        />
                        <Label htmlFor="remove-from-list" className="cursor-pointer">
                          Remove these IDs from my saved list afterwards
                        </Label>
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-background hover:bg-muted">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction onClick={handleApply} disabled={applying}>
                    {applying ? 'Applying…' : 'Apply to All'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {result && (
            <div className="flex items-start gap-3 rounded-md border border-border/50 bg-muted/20 p-3">
              <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium">
                  Applied to {result.updated.length} item(s)
                  {result.category ? ` · category “${result.category}”` : ''}
                  {result.findSubcategory ? ` · subcategory “${result.findSubcategory}”` : ''}
                  {result.top !== undefined ? ` · Top Visible: ${result.top ? 'yes' : 'no'}` : ''}
                </p>
                <p className="text-muted-foreground font-mono text-xs break-all mt-1">
                  {formatFindIds(result.updated)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3 — preview */}
      {parsedIds.length > 0 && (
        <Card className="bg-background border border-border/50 shadow-subtle">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">3. Preview</CardTitle>
            <CardDescription>
              {foundIds.size} item(s) found
              {missingIds.length > 0 ? ` · ${missingIds.length} ID(s) don't exist` : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingPreview && orderedMatches.length === 0 ? (
              <div className="flex items-center justify-center h-40">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading items…</span>
                </div>
              </div>
            ) : orderedMatches.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                None of the parsed IDs match an existing product.
              </p>
            ) : (
              <div className="rounded-md border max-h-[520px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">ID</TableHead>
                      <TableHead className="w-16">Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Current category</TableHead>
                      <TableHead className="text-right">Top</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderedMatches.map((item) => (
                      <TableRow key={item._id}>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          #{item._id}
                        </TableCell>
                        <TableCell>
                          <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center overflow-hidden">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.visibility = 'hidden';
                                }}
                              />
                            ) : (
                              <ImageOff className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium truncate max-w-[260px]">{item.name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[260px]">
                            {item.brand}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-background/50">
                            {item.category || '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.top ? (
                            <Badge variant="secondary">Visible</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {missingIds.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  IDs not found in the products table
                </Label>
                <div className="flex flex-wrap gap-2">
                  {missingIds.map((id) => (
                    <Badge key={id} variant="destructive" className="font-mono">
                      #{id}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkEdit;
